import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetSupportTicketCountsQueryKey,
  getGetSupportTicketsQueryKey,
  postSupportTickets,
  useGetSupportPartner,
} from '@/api/control/control-plane/control-plane';
import type { CreateSupportTicketRequestCategory } from '@/api/control/missionImpactAPI.schemas';
import { PageHeader } from '@/components/common/PageHeader';
import { AttachmentPicker } from '@/components/support/AttachmentPicker';
import { ConsultantHoursCard } from '@/components/support/ConsultantHoursCard';
import { SupportCoverageCard } from '@/components/support/SupportCoverageCard';
import { SupportNextStepsCard } from '@/components/support/SupportNextStepsCard';
import { WranglerRequestNote } from '@/components/support/WranglerRequestNote';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { useError } from '@/contexts/ErrorContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSupportAttachments } from '@/hooks/useSupportAttachments';
import { fillSupportTemplate } from '@/lib/support';

const TITLE_MAX_LENGTH = 200;

/** Navigation state passed to the ticket detail page after creation. */
export interface NewTicketNavigationState {
  failedUploads?: string[];
}

/**
 * Form for raising a new support ticket: the type of help (support or a Data Wrangler request),
 * title, description and optional attachments uploaded after creation. Alongside, it shows the
 * organization's Data Wrangler hours when a Data Wrangler request is chosen, and explains who helps
 * with what and what happens next, depending on whether the organization has a partner.
 */
export default function NewSupportTicketPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const { policy, validate, upload, hint } = useSupportAttachments(orgId ?? '');
  const { data: partnerResponse } = useGetSupportPartner(orgId ?? '', {
    query: { enabled: !!orgId, retry: false },
  });
  const partner = partnerResponse?.data ?? null;

  const [category, setCategory] = useState<CreateSupportTicketRequestCategory>('Support');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!orgId) {
    return null;
  }

  const supportUrl = `/dashboard/orgs/${orgId}/support`;
  const titleError = !title.trim()
    ? t.support.newTicket.titleRequired
    : title.trim().length > TITLE_MAX_LENGTH
      ? fillSupportTemplate(t.support.newTicket.titleTooLong, { max: TITLE_MAX_LENGTH })
      : null;
  const descriptionError = !description.trim() ? t.support.newTicket.descriptionRequired : null;
  const types: { value: CreateSupportTicketRequestCategory; label: string; description: string }[] = [
    { value: 'Support', label: t.support.newTicket.typeSupport, description: t.support.newTicket.typeSupportDescription },
    {
      value: 'DataWrangler',
      label: t.support.newTicket.typeWrangler,
      description: t.support.newTicket.typeWranglerDescription,
    },
  ];

  /**
   * Adds picked files that pass the attachment policy, reporting the rest.
   */
  const handlePick = (picked: File[]) => {
    const { accepted, errors } = validate(picked);
    setFiles((current) => [...current, ...accepted]);
    setFileErrors(errors);
  };

  /**
   * Creates the ticket, uploads each file, then opens the ticket.
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (titleError || descriptionError) return;

    setIsSubmitting(true);
    try {
      const response = await postSupportTickets(orgId, {
        title: title.trim(),
        description: description.trim(),
        category,
      });
      const ticketId = response.data.id;
      if (!ticketId) {
        return;
      }

      const { failed } = files.length > 0 ? await upload(ticketId, files) : { failed: [] as File[] };
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetSupportTicketsQueryKey(orgId) }),
        queryClient.invalidateQueries({ queryKey: getGetSupportTicketCountsQueryKey(orgId) }),
      ]);
      toast.success(t.support.newTicket.created);

      const state: NewTicketNavigationState = { failedUploads: failed.map((f) => f.name) };
      void navigate(`${supportUrl}/tickets/${ticketId}`, { state });
    } catch (error) {
      handleApiError(error, { context: 'NewSupportTicketPage.handleSubmit' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to={supportUrl}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t.support.newTicket.back}
        </Link>
      </Button>
      <PageHeader title={t.support.newTicket.title} description={t.support.newTicket.description} />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <Card className="min-w-0 self-start">
          <CardContent>
            <form className="space-y-5" noValidate onSubmit={(e) => { void handleSubmit(e); }}>
              <fieldset className="space-y-2">
                <legend className="mb-2 text-sm font-medium">{t.support.newTicket.typeLabel}</legend>
                <RadioGroup
                  value={category}
                  onValueChange={(value) => { setCategory(value as CreateSupportTicketRequestCategory); }}
                  className="grid gap-2 sm:grid-cols-2"
                >
                  {types.map((type) => (
                    <Label
                      key={type.value}
                      htmlFor={`support-type-${type.value}`}
                      className="flex cursor-pointer items-start gap-3 rounded-md border p-3 font-normal has-[[data-state=checked]]:border-primary"
                    >
                      <RadioGroupItem id={`support-type-${type.value}`} value={type.value} className="mt-0.5" />
                      <span className="space-y-1">
                        <span className="block font-medium">{type.label}</span>
                        <span className="block text-xs text-muted-foreground">{type.description}</span>
                      </span>
                    </Label>
                  ))}
                </RadioGroup>
                {category === 'DataWrangler' && <WranglerRequestNote partner={partner} />}
              </fieldset>
              <div className="space-y-2">
                <Label htmlFor="support-title">{t.support.newTicket.titleLabel}</Label>
                <Input
                  id="support-title"
                  value={title}
                  maxLength={TITLE_MAX_LENGTH}
                  placeholder={t.support.newTicket.titlePlaceholder}
                  aria-invalid={submitted && !!titleError}
                  onChange={(e) => { setTitle(e.target.value); }}
                />
                {submitted && titleError && <p className="text-sm text-destructive">{titleError}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="support-description">{t.support.newTicket.descriptionLabel}</Label>
                <Textarea
                  id="support-description"
                  rows={8}
                  value={description}
                  placeholder={t.support.newTicket.descriptionPlaceholder}
                  aria-invalid={submitted && !!descriptionError}
                  aria-describedby="support-description-hint"
                  onChange={(e) => { setDescription(e.target.value); }}
                />
                <p id="support-description-hint" className="text-xs text-muted-foreground">
                  {t.support.newTicket.descriptionHint}
                </p>
                {submitted && descriptionError && <p className="text-sm text-destructive">{descriptionError}</p>}
              </div>
              <div className="space-y-2">
                <Label>{t.support.newTicket.attachmentsLabel}</Label>
                {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
                <AttachmentPicker
                  files={files}
                  accept={policy?.allowedExtensions}
                  buttonLabel={t.support.detail.addFiles}
                  disabled={isSubmitting}
                  onPick={handlePick}
                  onRemove={(file) => { setFiles((current) => current.filter((f) => f !== file)); }}
                />
                {fileErrors.length > 0 && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      {fileErrors.map((message) => (
                        <div key={message}>{message}</div>
                      ))}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t.support.newTicket.submitting : t.support.newTicket.submit}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={() => { void navigate(supportUrl); }}
                >
                  {t.support.newTicket.cancel}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        <aside className="space-y-6">
          {category === 'DataWrangler' && <ConsultantHoursCard orgId={orgId} />}
          <SupportCoverageCard partner={partner} />
          <SupportNextStepsCard partner={partner} />
        </aside>
      </div>
    </div>
  );
}
